/**
 * Module dependencies.
 */

import { apiEndpoints } from 'app/core/config/api-endpoints';
import { appRoutes } from 'app/routes';
import { createQueryClient } from 'app/core/utils/react-query';
import { Button, Drawer, Modal } from 'antd';
import { identity, pickBy } from 'lodash';
import { CrudListType, CrudTemplate, ListColumns } from '../../../organisms/crud/form-types';
import { QueryClientProvider, useMutation } from '@tanstack/react-query';
import { TFunction } from 'react-i18next';
import { ListFilters } from 'app/components/organisms/crud/form-filters/form-filters-type';
import { DateTime } from 'app/components/atoms/date-time/date-time';
import { IdCopy } from 'app/components/atoms/id-copy/id-copy';
import { LogBodyRenderer } from './log-body-renderer';
import { LogDetail } from './log-detail';
import { useGetLogVersions } from 'app/hooks/app-requests/logs/use-get-log-versions';
import { useGetLogTypes } from 'app/hooks/app-requests/logs/use-get-log-types';
import { useGetLogModules } from 'app/hooks/app-requests/logs/use-get-log-modules';
import { resolveAppUrl } from 'app/core/utils/url-resolver';
import { DeleteOutlined } from '@ant-design/icons';
import { useSearchParams } from 'react-router-dom';
import RequestProvider, { useRequestProvider } from 'app/components/providers/request-provider/request-provider';
import moment from 'moment';
import React, { useState } from 'react';
import ReactDOM from 'react-dom/client';

/**
 * `ZLog` type.
 */

type ZLog = {
  id: string;
  correlationId: string;
  userId: string | null;
  type: string;
  level: string;
  body: object;
  version: string;
  moduleName: string;
  createdAt: string;
};

/**
 * List labels.
 */

const listColumns = (): ListColumns<ZLog> => [
  {
    title: 'ID',
    dataIndex: 'id',
    key: 'id',
    render: item => <IdCopy>{String(item.id)}</IdCopy>
  },
  {
    title: 'Correlation ID',
    dataIndex: 'correlationId',
    key: 'correlationId',
    render: item => <IdCopy>{String(item.correlationId)}</IdCopy>
  },
  {
    title: 'Level',
    dataIndex: 'level',
    key: 'level'
  },
  {
    title: 'Version',
    dataIndex: 'version',
    key: 'version'
  },
  {
    title: 'Module',
    dataIndex: 'moduleName',
    key: 'moduleName'
  },
  {
    title: 'Type',
    dataIndex: 'type',
    key: 'type'
  },
  {
    title: 'Body',
    dataIndex: 'body',
    size: '1fr',
    key: 'body',
    render: item => <LogBodyRenderer body={item.body} type={item.type} />
  },
  {
    title: 'Created At',
    dataIndex: 'createdAt',
    key: 'createdAt',
    render: value => <DateTime date={value.createdAt} showMilliseconds />
  }
];

/**
 * List filters.
 */

const toIso = (value: any): string | undefined => (value ? moment(value).toISOString() : undefined);

const listFilters = (translate: TFunction): ListFilters => [
  {
    type: 'inputSearch',
    inline: true,
    name: 'search',
    label: translate('common.labels.search'),
    style: { flexBasis: '100%', marginBottom: '8px' }
  },
  {
    type: 'datePickerSearch',
    inline: true,
    name: 'fromDate',
    label: translate('common.labels.startDate'),
    disabledDate: (date, values) => {
      if (values?.toDate && date.isAfter(moment(values.toDate), 'day')) {
        return true;
      }

      return date.isAfter(moment(), 'day');
    }
  },
  {
    type: 'datePickerSearch',
    inline: true,
    name: 'toDate',
    label: translate('common.labels.endDate'),
    disabledDate: date => date.isAfter(moment(), 'day')
  },
  {
    type: 'selectSearch',
    name: 'level',
    inline: true,
    label: translate('common.labels.level'),
    showSearch: true,
    allowClear: true,
    options: [
      {
        label: translate('common.labels.verbose'),
        value: 'verbose'
      },
      {
        label: translate('common.labels.info'),
        value: 'info'
      },
      {
        label: translate('common.labels.warn'),
        value: 'warn'
      },
      {
        label: translate('common.labels.error'),
        value: 'error'
      },
      {
        label: translate('common.labels.table'),
        value: 'table'
      }
    ]
  },
  {
    type: 'selectSearch',
    name: 'version',
    inline: true,
    label: translate('common.labels.version'),
    showSearch: true,
    allowClear: true,
    options: {
      hook: useGetLogVersions,
      hookProps: (values: any) => [toIso(values?.fromDate), toIso(values?.toDate)],
      normalize: (versions: string[]) => {
        if (!Array.isArray(versions)) {
          return [];
        }

        return versions.map(version => ({
          value: version,
          label: version,
          searchValue: version
        }));
      }
    }
  },
  {
    type: 'selectSearch',
    name: 'type',
    inline: true,
    label: translate('common.labels.type'),
    showSearch: true,
    allowClear: true,
    options: {
      hook: useGetLogTypes,
      hookProps: (values: any) => [toIso(values?.fromDate), toIso(values?.toDate)],
      normalize: (types: string[]) => {
        if (!Array.isArray(types)) {
          return [];
        }

        return types.map(type => ({
          value: type,
          label: type,
          searchValue: type
        }));
      }
    }
  },
  {
    type: 'selectSearch',
    name: 'moduleName',
    inline: true,
    label: translate('common.labels.module'),
    showSearch: true,
    allowClear: true,
    options: {
      hook: useGetLogModules,
      hookProps: (values: any) => [toIso(values?.fromDate), toIso(values?.toDate)],
      normalize: (modules: string[]) => {
        if (!Array.isArray(modules)) {
          return [];
        }

        return modules.map(module => ({
          value: module,
          label: module,
          searchValue: module
        }));
      }
    }
  },
  {
    type: 'liveToggle',
    inline: true,
    label: 'Auto Refresh'
  }
];

/**
 * Delete Logs Button Component.
 */

function DeleteLogsButton({ refresh }: { refresh: () => Promise<any> }) {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const client = useRequestProvider();
  const [searchParams] = useSearchParams();

  // Get current filters from URL search params
  // Filters are stored as a JSON string in the 'filter' parameter
  const filterParam = searchParams.get('filter');
  const filters = filterParam ? JSON.parse(filterParam) : {};
  const moduleName = filters.moduleName;

  const deleteMutation = useMutation({
    mutationFn: async () => {
      let url = resolveAppUrl(apiEndpoints.logsDelete);
      if (moduleName) {
        url = `${url}?moduleName=${encodeURIComponent(moduleName)}`;
      }
      const response = await client.delete(url);
      return response.data;
    },
    onSuccess: async () => {
      await refresh();
      Modal.success({
        title: 'Success',
        content: 'Logs deleted successfully'
      });
      setIsModalVisible(false);
    },
    onError: (error: any) => {
      Modal.error({
        title: 'Error',
        content: error?.response?.data?.message || 'Failed to delete logs'
      });
    }
  });

  const handleDelete = () => {
    deleteMutation.mutate();
  };

  const modalContent = moduleName
    ? `Are you sure you want to delete all logs from the "${moduleName}" module? This action cannot be undone.`
    : 'Are you sure you want to delete ALL logs? This action cannot be undone.';

  return (
    <>
      <Button
        danger
        icon={<DeleteOutlined />}
        loading={deleteMutation.isLoading}
        onClick={() => setIsModalVisible(true)}
        size={'large'}
      >
        {'Delete Logs '}
        {moduleName ? `(${moduleName})` : '(All)'}
      </Button>
      <Modal
        cancelText={'Cancel'}
        okButtonProps={{ danger: true, loading: deleteMutation.isLoading }}
        okText={'Delete'}
        onCancel={() => setIsModalVisible(false)}
        onOk={handleDelete}
        open={isModalVisible}
        title={'Confirm Delete'}
      >
        <p>{modalContent}</p>
      </Modal>
    </>
  );
}

/**
 * List.
 */

function list(translate: TFunction): CrudListType<ZLog> {
  return {
    columns: listColumns(),
    filters: listFilters(translate),
    route: appRoutes.dashboard.developer.logs,
    enableExport: true,
    canEdit: false,
    canRemove: false,
    extraActions: ({ refresh }) => <DeleteLogsButton refresh={refresh} />,
    clickLine: (item: ZLog) => {
      // Create a container for the drawer
      const container = document.createElement('div');
      document.body.appendChild(container);
      const root = ReactDOM.createRoot(container);
      const queryClient = createQueryClient(
        () => {
          // Empty error handler for drawer context
        },
        () => {
          // Empty success handler for drawer context
        }
      );

      const handleClose = () => {
        root.unmount();
        document.body.removeChild(container);
      };

      root.render(
        <QueryClientProvider client={queryClient}>
          <RequestProvider client={queryClient}>
            <Drawer
              bodyStyle={{
                paddingTop: 24,
                height: '100%',
                overflow: 'auto'
              }}
              closable={true}
              destroyOnClose={true}
              onClose={handleClose}
              open={true}
              placement={'right'}
              title={translate('common.labels.logDetails')}
              width={'60%'}
            >
              <LogDetail item={item} />
            </Drawer>
          </RequestProvider>
        </QueryClientProvider>
      );
    },
    resolveParams: (params: any, item?: ZLog) => {
      return {
        id: params.id ?? item?.id
      };
    },
    key: ['logs-api'],
    normalizeFilters: (data: any) => {
      const { liveMode, ...rest } = data;
      const { fromDate, toDate, ...filters } = rest.filters || {};

      return {
        filters: pickBy(filters, identity),
        order: data.order && data.order.length > 0 ? data.order : [{ key: 'createdAt', direction: 'desc' }],
        liveMode,
        ...(fromDate ? { fromDate: moment(fromDate).startOf('day').toISOString() } : {}),
        ...(toDate ? { toDate: moment(toDate).endOf('day').toISOString() } : {})
      };
    },
    normalizeData: (result: any) => {
      return result;
    },
    pagination: {
      pageSize: 100,
      pageSizeOptions: [10, 25, 50, 100, 250],
      pagingByTime: true,
      timeField: 'createdAt'
    },
    endpoint: apiEndpoints.logs,
    endpointById: apiEndpoints.logsById
  };
}

/**
 * Config.
 */

export function createLogsTemplate(translate: TFunction): CrudTemplate<ZLog> {
  return {
    list: list(translate)
  };
}
